import React, {Component} from 'react';
import ResourceException from './ResourceException';
import ServerValidationException from './ServerValidationException';
import queryString from 'query-string';
import {store} from '../store/store';
import * as resourceActions from '../actions/resource/resourceActions';
import * as spinnerActions from '../actions/spinnerActions';
import * as maintenanceModeActions from '../actions/common/maintenanceModeActions';
import RedirectHelper from "./RedirectHelper";
import ServerException from "../components/services/ServerException";


const HttpSuccessStatuses = [200, 201, 202, 203, 204];
const HttpClientErrorsStatuses = [400, 401, 402, 403, 404, 405, 500];
export const MAINTENANCE_MODE_STATUS = 503;
export const AUTHENTICATION_ERROR = 401;

/**
 * Represents RESTful resource.
 * Does some routine work to communicate with server
 */
class Resource {

    static all(resource, data = {}) {
        let fullResourse = `${this.prefix()}/${resource}`;

        let newData = {};
        // filter undefined values
        Object.keys(data).forEach(key => {
            if (data[key] !== undefined) {
                newData[key] = data[key];
            }
        });

        let params = Object.keys(newData).map(key => {
            return `${key}=${newData[key]}`
        });

        if (params.length) {
            if (fullResourse.indexOf('?') === -1) {
                fullResourse += '?';
            }
            else {
                fullResourse += '&';
            }
            fullResourse += params.join('&');
        }


        return fetch(fullResourse, {credentials: 'include'})

            .then(response => {
                return this._processResponse(response);
            })

    }


    static get(resource, id = null, data = {},) {

        let url = id === null ? `${this.prefix()}/${resource}` : `${this.prefix()}/${resource}/${id}`;

        if (Object.keys(data).length !== 0) {
            let paramsDivider;
            if (url.indexOf('?') === -1) {
                paramsDivider = '?';
            }
            else {
                paramsDivider = '&';
            }

            url = `${url}${paramsDivider}${queryString.stringify(data)}`;
        }

        return fetch(url, {
            method: 'GET',
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response, response.status);
        })
    }

    static getNested(resourceA, resourceB, id, data = {}) {
        let url = `${this.prefix()}/${resourceA}/${id}/${resourceB}`;
        if (Object.keys(data).length !== 0) {
            let paramsDivider;
            if (url.indexOf('?') === -1) {
                paramsDivider = '?';
            }
            else {
                paramsDivider = '&';
            }

            url = `${url}${paramsDivider}${queryString.stringify(data)}`;
        }

        return fetch(url, {
            method: 'GET',
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response, response.status);
        })
    }

    /**
     * Serializes and sends json to the server with POST method.
     *
     * @param  {string}           resource         Short representation of some
     *                                             RESTful resource. No slashes.
     *                                             Example: user
     * @param  {object}           data
     * @param  {boolean}          isFormData       If is formData we need special format
     * @return {Promise.<object>}
     */
    static create(resource, data = {}, isFormData = false) {
        let headers = {
            'Accept': 'application/json, text/plain, */*',
        };

        if (!isFormData) {
            data = JSON.stringify(data);
            headers['Content-Type'] = 'application/json';
        }

        return fetch(`${this.prefix()}/${resource}`, {
            method: 'POST',
            body: data,
            headers: headers,
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response);
        })
    }

    static put(resource, data, isFormData = false) {
        let headers = {
            'Accept': 'application/json, text/plain, */*',
        };

        if (!isFormData) {
            data = JSON.stringify(data);
            headers['Content-Type'] = 'application/json';
        }

        return fetch(`${this.prefix()}/${resource}`, {
            method: 'PUT',
            body: data,
            headers: headers,
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response);
        })
    }

    /**
     * Serializes data and updates the server resource with PUT method by id.
     *
     * @param  {string}           resource         Short representation of some
     *                                             RESTful resource. No slashes.
     *                                             Example: user
     * @param  {int}              id
     * @param  {object}           data
     * @param  {boolean}          isFormData       If is formData we need special format
     * @return {Promise.<object>}
     */
    static update(resource, id, data, isFormData = false) {
        let headers = {
            'Accept': 'application/json, text/plain, */*',
            // 'Content-Type': 'application/x-www-form-urlencoded',
        };

        if (!isFormData) {
            data = JSON.stringify(data);
            headers['Content-Type'] = 'application/json';
        }

        return fetch(`${this.prefix()}/${resource}/${id}`, {
            method: 'PUT',
            body: data,
            headers: headers,
            credentials: 'include'

        }).then(response => {
            return this._processResponse(response);
        })
    }

    /**
     * Deletes remote object via DELETE method.
     *
     * @param  {string}           resource         Short representation of some
     *                                             RESTful resource. No slashes.
     *                                             Example: user
     * @param  {int}              id
     * @return {Promise.<int>}
     */
    static remove(resource, id) {
        return fetch(`${this.prefix()}/${resource}/${id}`, {
            method: 'DELETE',
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response);
        })
    }

    static options(resource, id = null, data = {},) {

        let url = id === null ? `${this.prefix()}/${resource}` : `${this.prefix()}/${resource}/${id}`;

        if (Object.keys(data).length !== 0) {
            url = `${url}?${queryString.stringify(data)}`;
        }

        return fetch(url, {
            method: 'OPTIONS',
            credentials: 'include'
        }).then(response => {
            return this._processResponse(response);
        })
    }

    static prefix() {
        let prefix = '';

        if (APP_DOMAIN === undefined) {
            throw new Error("You have to specify env variable APP_DOMAIN with a protocol and a hostname.");
        }

        if (BASE_URL) {
            prefix = BASE_URL.endsWith('/') ? BASE_URL.substring(0, BASE_URL.length - 2) : BASE_URL;
        }

        return APP_DOMAIN + prefix;
    }

    /**
     *
     * @param response
     * @private
     */
    static _processResponse(response) {

        /**
         * Sometimes it encounters this issue https://github.com/github/fetch/issues/268
         */
        let jsonResponse = response.text().then(function(text) {
            return text ? JSON.parse(text) : {}
        });

        this._checkMaintenanceMode(response.status);
        //this._processAuthenticationError(response);
        return this._processError(jsonResponse, response.status);
    }

    static _processAuthenticationError(response) {
        if (response.status === AUTHENTICATION_ERROR) {
            response.json().then(data => {
                //console.log( data.reloginPath + '?ref=' + encodeURIComponent(window.location.href));
                window.location.href =  data.reloginPath + '?ref=' + encodeURIComponent(window.location.href);
            });
            // RedirectHelper.storeUrl();
            // store.dispatch(resourceActions.showTokenValidationFailedModal());
            // store.dispatch(spinnerActions.hideSpinner());
            // return Promise.resolve(1);
        }
    }

    static _checkMaintenanceMode(status) {
        if (status === MAINTENANCE_MODE_STATUS) {
            store.dispatch(spinnerActions.hideSpinner());
            store.dispatch(maintenanceModeActions.triggerMaintenanceMode(true));
        }
    }

    /**
     *
     * @param {Promise} response
     * @param {Number} status
     * @private
     */
    static _processError(response, status) {
        if (HttpSuccessStatuses.includes(status)) {
            return response;
        }
        if (HttpClientErrorsStatuses.includes(status)) {
            return response.then((error) => {
                if (status === AUTHENTICATION_ERROR) {
                    window.location.href = error.reloginPath + '?ref=' + encodeURIComponent(window.location.href);
                    throw new ServerException(error.message, status);
                }

                if (error.message) {
                    throw new ServerException(error.message, status);
                } else {
                    throw new ServerValidationException(error, status);
                }
            });
        }
        else {
            throw new ResourceException(`Resource error`)
        }
    }

}

export default Resource;


// WEBPACK FOOTER //
// ./web/src/services/Resource.js